import type { ForqSupabaseClient } from './client';
import {
  type AddAttachmentDocumentInput,
  type AttachmentDocumentCategory,
  type AttachmentDocumentGateway,
  type AttachmentDocumentRecord,
  type UpdateAttachmentDocumentInput,
} from '@forq/platform-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

const CATEGORIES: readonly AttachmentDocumentCategory[] = ['checklist', 'protocol', 'form', 'other'];

function toRecord(value: unknown): AttachmentDocumentRecord {
  const raw = (value ?? {}) as Record<string, unknown>;
  const category = raw.category as AttachmentDocumentCategory;
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    category: CATEGORIES.includes(category) ? category : 'other',
    mimeType: String(raw.mimeType ?? ''),
    byteSize: Number(raw.byteSize ?? 0),
    alwaysOffer: raw.alwaysOffer === true,
    keywords: Array.isArray(raw.keywords) ? raw.keywords.map((keyword) => String(keyword)) : [],
    createdAt: String(raw.createdAt ?? ''),
    content: typeof raw.content === 'string' ? raw.content : '',
  };
}

/**
 * Zusatzdokumente / Prüflisten gegen Supabase. Metadaten liest jedes Mandantenmitglied mit
 * Vorgangs-Sicht (für die Werkstattauftrag-Abfrage); pflegen darf nur die Verwaltung -- das trennen
 * die RPC-Rechte (orders.view zum Lesen, admin.view zum Ändern).
 */
export class SupabaseAttachmentDocumentGateway implements AttachmentDocumentGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async listDocuments(tenantId: string): Promise<readonly AttachmentDocumentRecord[]> {
    const { data, error } = await this.client.rpc('attachment_document_list', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('documents.attachment_document_list', messageOf(error));
    return Array.isArray(data) ? data.map(toRecord) : [];
  }

  async getDocument(tenantId: string, documentId: string): Promise<AttachmentDocumentRecord> {
    const { data, error } = await this.client.rpc('attachment_document_get', {
      p_tenant_id: tenantId,
      p_document_id: documentId,
    });
    if (error) throw new SupabaseOperationError('documents.attachment_document_get', messageOf(error));
    return toRecord(data);
  }

  async addDocument(input: AddAttachmentDocumentInput): Promise<AttachmentDocumentRecord> {
    const { tenantId, ...payload } = input;
    const { data, error } = await this.client.rpc('attachment_document_add', {
      p_tenant_id: tenantId,
      p_payload: payload,
    });
    if (error) throw new SupabaseOperationError('documents.attachment_document_add', messageOf(error));
    return toRecord(data);
  }

  async updateDocument(input: UpdateAttachmentDocumentInput): Promise<AttachmentDocumentRecord> {
    const { tenantId, documentId, ...payload } = input;
    const { data, error } = await this.client.rpc('attachment_document_update', {
      p_tenant_id: tenantId,
      p_document_id: documentId,
      p_payload: payload,
    });
    if (error) throw new SupabaseOperationError('documents.attachment_document_update', messageOf(error));
    return toRecord(data);
  }

  async removeDocument(tenantId: string, documentId: string): Promise<void> {
    const { error } = await this.client.rpc('attachment_document_remove', {
      p_tenant_id: tenantId,
      p_document_id: documentId,
    });
    if (error) throw new SupabaseOperationError('documents.attachment_document_remove', messageOf(error));
  }
}
